import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Show } from "@clerk/react";
import { CONTENT } from "@/content";
import { fmtDate, useApi } from "@/lib/api";

const M = CONTENT.myShares;
const NAMES = Object.fromEntries(CONTENT.share.platforms);
const reached = (n) => `${n} ${n === 1 ? M.reachedOne : M.reachedWord}`;
const sharesTxt = (n) => `${n} ${n === 1 ? M.shareOne : M.sharesWord}`;

const Stats = ({ stats }) => (
  <div className="how-grid">
    {[stats.shares, stats.reached, stats.platforms].map((n, i) => (
      <div key={i} className="nb-card stat-card" data-testid={`my-stat-${i + 1}`}><span className="stat-num">{n}</span><span className="field-label">{M.stats[i]}</span></div>
    ))}
  </div>
);

const RippleVisual = ({ reached: n }) => (
  <div className="nb-card ripple-card" data-testid="my-ripple-visual">
    <div className="ripple-static" aria-hidden="true">{[1, 2, 3, 4].map((r) => <span key={r} className={r % 2 ? "ring-black" : "ring-orange"} style={{ "--s": r }} />)}</div>
    <div className="ripple-center"><span className="stat-num">{n}</span><span className="field-label">{n === 1 ? M.reachedOne : M.reachedWord}</span></div>
  </div>
);

const Platforms = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.shares));
  return (
    <div className="nb-card bars" data-testid="my-by-platform">
      <h2 className="admin-h2">{M.byPlatform}</h2>
      {rows.map((r) => (
        <div key={r.platform} className="bar-row">
          <span className="bar-label">{NAMES[r.platform]}</span>
          <span className="bar-track"><span className="bar-fill" style={{ width: `${(r.shares / max) * 100}%` }} /></span>
          <span className="bar-meta">{sharesTxt(r.shares)}, {reached(r.reached)}</span>
        </div>
      ))}
    </div>
  );
};

const Content = () => {
  const api = useApi();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => { api("get", "/me/shares", null, { page }).then(setData).catch(() => setData(null)); }, [api, page]);
  if (!data) return null;
  if (!data.total) return (
    <div className="nb-card cta-card" data-testid="my-shares-empty"><p className="panel-line">{M.empty}</p><Link to="/library" className="nb-btn">{M.emptyButton}</Link></div>
  );
  const pages = Math.ceil(data.total / data.per_page);
  return (
    <>
      <Stats stats={data.stats} />
      <RippleVisual reached={data.stats.reached} />
      <Platforms rows={data.by_platform} />
      <h2 className="admin-h2">{M.whatIShared}</h2>
      <ul className="content-list" data-testid="my-shares-list">
        {data.items.map((s) => (
          <li key={s.id}>
            <Link to={`/library/${s.slug}`} className="nb-card content-row share-item" data-testid={`my-share-${s.id}`}>
              <div className="row-main"><h3 className="row-title">{s.title}</h3><p className="row-ref">{NAMES[s.platform]} {M.on} {fmtDate(s.created_at)}, {reached(s.reached)}</p></div>
              <span className="verse-ref">{s.reference}</span>
            </Link>
          </li>
        ))}
      </ul>
      {pages > 1 && (
        <div className="pager">
          <button className="nb-btn ghost small" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="my-shares-prev">{M.prev}</button>
          <span className="field-label">{page} / {pages}</span>
          <button className="nb-btn ghost small" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="my-shares-next">{M.next}</button>
        </div>
      )}
    </>
  );
};

export default function MyShares() {
  return (
    <main className="page library" data-testid="my-shares-page">
      <header className="journey-head">
        <span className="pill">{M.label}</span>
        <h1 className="heart-title">{M.title}</h1>
        <p className="heart-lead">{M.supporting}</p>
      </header>
      <Show when="signed-in"><Content /></Show>
      <Show when="signed-out"><Navigate to="/account" replace /></Show>
    </main>
  );
}
